import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly codeVerifier = this.generateCodeVerifier();
  private readonly debug = !environment.production;

  constructor(private http: HttpClient) {}

  async redirect(): Promise<void> {
    if (this.debug) console.log('[redirect]: codeVerifier', this.codeVerifier);

    let redirURL: string;
    if (!environment.useAcme) {
      redirURL = `https://idp.${environment.myIDenv}.disney.com/as/authorization.oauth2?client_id=${environment.clientID}&response_type=id_token+token&redirect_uri=${environment.redirect}&nonce=APPLICATION_GENERATED_ONE_TIME_NONCE&scope=openid+profile+email+relationship.employeeNumber+relationship.employeeId`;
    } else {
      const codeChallenge = await this.generateCodeChallenge(this.codeVerifier);
      const params = {
        response_type: 'code',
        scope: 'openid address email id profile relationship',
        redirect_uri: this.retrieveRedirectUrl() ?? environment.redirect,
        client_id: environment.acmeClientID,
        state: btoa(this.codeVerifier),
        code_challenge: codeChallenge,
        code_challenge_method: 's256',
      };
      redirURL = `${environment.acmeAuthorizeUrl}?${new URLSearchParams(params).toString()}`;
    }
    document.location.href = redirURL;
  }

  retrieveAcmeToken(authCode: string) {
    if (this.debug) console.log('[retrieveAcmeToken]: codeVerifier', this.codeVerifier);
    const data = new URLSearchParams({
      scope: 'openid address email id profile relationship',
      redirect_uri: this.retrieveRedirectUrl() ?? environment.redirect,
      client_id: environment.acmeClientID,
      grant_type: 'authorization_code',
      code: authCode,
      code_verifier: this.codeVerifier,
    }).toString();
    return this.http.post<{ access_token: string; id_token: string }>(
      environment.acmeTokenUrl,
      data
    );
  }

  private generateCodeVerifier(length = 64): string {
    const storageKey = `${environment.tokenStorageName}-codeVerifier`;
    let codeVerifier = localStorage.getItem(storageKey);
    if (!codeVerifier) {
      const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
      let text = '';
      for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
      }
      codeVerifier = text;
      localStorage.setItem(storageKey, codeVerifier);
    }
    return codeVerifier;
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private retrieveRedirectUrl(): string | null {
    const storageKey = `cast-profile-${environment.env}-redirect`;
    const redirectUrl = sessionStorage.getItem(storageKey);
    if (!redirectUrl) {
      sessionStorage.setItem(storageKey, environment.redirect);
    }
    return redirectUrl;
  }
}
